import { computed, ref, watch } from 'vue'
import { useRecordingLibrary } from './useRecordingLibrary'
import { useVadSpeechRecorder } from './useVadSpeechRecorder'
import { useVadVisualizer } from './useVadVisualizer'
import { useMicrophoneDevices } from './useMicrophoneDevices'
import { useHighQualityRecorder } from './useHighQualityRecorder'
import { useLiveSpeechProgress } from './useLiveSpeechProgress'
import { requiredPauseMs } from '@/utils/speech/readAlongEndpoint'
import { isReadAlongPreferred, saveReadAlongPreference } from './readAlongPreference'

// Silero v5 thresholds, kept in sync with useVadSpeechRecorder.
const SPEECH_PROBABILITY = 0.45
const SILENCE_PROBABILITY = 0.3
const PRE_ROLL_MS = 500
const COUNTDOWN_VISIBLE_AFTER_MS = 250
// Short sentences often end before the last words come back; keep them on screen a moment.
const LIVE_CAPTION_LINGER_MS = 2000
const FINISHED_PAUSE_STORAGE_KEY = 'readAlongFinishedPauseMs'
const UNFINISHED_PAUSE_STORAGE_KEY = 'readAlongUnfinishedPauseMs'
// The VAD's own silence rule is only a safety net; trackAttemptFrame decides the end.
const VAD_PAUSE_MARGIN_MS = 2500
// Retelling ends by hand: no silence ends the attempt, only the recording limit does.
const RETELLING_PAUSE_MS = 10 * 60 * 1000

export function useReadAlong({
  sound,
  isPlaying,
  currentSubtitleIndex,
  displaySubtitles,
  pauseCurrent,
  seekCurrent,
  audioVisualizerRef,
  showError,
  onRecordingReady,
  recordingSessionKey,
  getMaxRecordingMs = () => 60000,
  getRecognitionLanguage = () => 'en-US',
  // Live recognition comes with pronunciation scoring; without it the attempt ends by duration.
  liveRecognitionEnabled = ref(false),
}) {
  const isReadAlongMode = ref(isReadAlongPreferred())
  const readAlongMode = ref('recording')
  const isRecording = ref(false)
  const isPaused = ref(false)
  const isWaitingForShadowing = ref(false)
  const isPausedWhileShadowing = ref(false)
  const waitingForRecording = ref(false)

  const pauseTimer = ref(null)
  const waitTimer = ref(null)
  const shadowingTimer = ref(null)
  const speechWaitTimer = ref(null)
  const recordingLimitTimer = ref(null)
  const followingInterval = ref(1)
  // Pause that ends an attempt once the sentence looks finished (user adjustable).
  const vadRedemptionMs = ref(Number(window.localStorage.getItem(FINISHED_PAUSE_STORAGE_KEY)) || 800)
  // Pause that ends an attempt while the sentence is unfinished, or when nothing tells
  // how far the learner has read (user adjustable).
  const unfinishedPauseMs = ref(Number(window.localStorage.getItem(UNFINISHED_PAUSE_STORAGE_KEY)) || 3500)
  // 'recording' and 'retelling' both record the learner. In retelling the learner ends
  // each attempt themselves (button or Enter) and nothing is recognised or scored.
  const isRetelling = computed(() => readAlongMode.value === 'retelling')
  // 0..1 while the learner is silent: how close the attempt is to ending.
  const pauseProgress = ref(0)
  const isHoldingForUnfinished = ref(false)
  // What live recognition heard in the current attempt, shown to the learner.
  const liveTranscript = ref('')
  const isLiveCaptionLingering = ref(false)
  let liveCaptionTimer = null
  const maxSpeechWaitMs = 60000
  const shadowingStartTime = ref(0)
  const shadowingDuration = ref(0)

  const recordingLibrary = useRecordingLibrary({ sessionKey: recordingSessionKey })
  const visualizer = useVadVisualizer({ readAlongMode, isRecording, audioVisualizerRef })
  const microphones = useMicrophoneDevices()
  const originalRecorder = useHighQualityRecorder({
    getStream: microphones.getSharedStream,
  })
  const liveSpeech = useLiveSpeechProgress()
  const usesLiveRecognition = computed(() => liveRecognitionEnabled.value && !isRetelling.value)
  const liveRecognitionStatus = computed(() => (usesLiveRecognition.value ? liveSpeech.status.value : 'off'))

  let playNextSubtitle = null
  let activeRecordingIndex = null
  let isCompletingRecording = false
  let isSystemPauseForRecording = false
  let recordingPreparationPromise = null
  let attempt = createAttempt()

  function createAttempt() {
    return {
      preRoll: [],
      started: false,
      ending: false,
      session: null,
      elapsedMs: 0,
      lastSpeechMs: 0,
      silenceMs: 0,
    }
  }

  function resetAttempt() {
    attempt.session?.cancel()
    attempt = createAttempt()
    pauseProgress.value = 0
    isHoldingForUnfinished.value = false
  }

  function expectedSpeechMs(index) {
    const subtitle = displaySubtitles.value[index]
    if (!subtitle) return 0
    return Math.max(0, (Number(subtitle.end) - Number(subtitle.start)) * 1000)
  }

  function liveProgress() {
    const session = attempt.session
    return session && !session.failed ? session.progress : null
  }

  // Connect live recognition as soon as the learner is asked to speak, so the first
  // words show up without waiting for a token or a connection.
  function openLiveSession() {
    const subtitle = displaySubtitles.value[activeRecordingIndex]
    if (!usesLiveRecognition.value || !subtitle?.text) return
    attempt.session = liveSpeech.openSession({
      referenceText: subtitle.text,
      language: getRecognitionLanguage(),
      onHeard: (text) => {
        liveTranscript.value = text
      },
    })
  }

  function lingerLiveCaption() {
    clearTimeout(liveCaptionTimer)
    isLiveCaptionLingering.value = true
    liveCaptionTimer = setTimeout(() => {
      isLiveCaptionLingering.value = false
    }, LIVE_CAPTION_LINGER_MS)
  }

  function beginAttempt() {
    clearTimeout(liveCaptionTimer)
    isLiveCaptionLingering.value = false
    attempt.started = true
    liveTranscript.value = ''
    attempt.session?.begin()
    attempt.preRoll.splice(0).forEach((frame) => attempt.session?.pushFrame(frame))
  }

  function trackAttemptFrame(probabilities, frame) {
    if (!waitingForRecording.value || attempt.ending) return
    const frameMs = frame.length / 16

    if (!attempt.started) {
      attempt.preRoll.push(frame)
      while (attempt.preRoll.length * frameMs > PRE_ROLL_MS) attempt.preRoll.shift()
      return
    }

    attempt.session?.pushFrame(frame)
    attempt.elapsedMs += frameMs
    const probability = probabilities?.isSpeech || 0
    if (probability >= SPEECH_PROBABILITY) {
      attempt.lastSpeechMs = attempt.elapsedMs
      attempt.silenceMs = 0
    } else if (probability < SILENCE_PROBABILITY) {
      attempt.silenceMs += frameMs
    }
    if (isRetelling.value) return

    const progress = liveProgress()
    const required = requiredPauseMs({
      progress,
      speechMs: attempt.lastSpeechMs,
      expectedMs: expectedSpeechMs(activeRecordingIndex),
      finishedPauseMs: vadRedemptionMs.value,
      unfinishedPauseMs: unfinishedPauseMs.value,
    })
    const visibleSilence = attempt.silenceMs >= COUNTDOWN_VISIBLE_AFTER_MS
    pauseProgress.value = visibleSilence ? Math.min(1, attempt.silenceMs / required) : 0
    isHoldingForUnfinished.value = visibleSilence && required === unfinishedPauseMs.value

    if (attempt.silenceMs >= required) finishSpeaking()
  }

  // Ends the current attempt now; the VAD hands back the audio through onSpeechEnd.
  function finishSpeaking() {
    if (!isRecording.value || attempt.ending) return
    attempt.ending = true
    pauseProgress.value = 0
    isHoldingForUnfinished.value = false
    void vadRecorder.pause().catch(() => {})
  }

  const vadRecorder = useVadSpeechRecorder({
    onSpeechStart: () => {
      if (!waitingForRecording.value || readAlongMode.value === 'following') return
      try {
        originalRecorder.start()
      } catch {}
      clearSpeechWaitTimer()
      setupRecordingLimit()
      isRecording.value = true
      beginAttempt()
    },
    onSpeechEnd: (audio) => finishVadRecording(audio),
    onVADMisfire: (audio) => {
      if (audio?.length) {
        finishVadRecording(audio)
        return
      }
      originalRecorder.cancel()
      clearRecordingLimitTimer()
      isRecording.value = false
      // A manual or rule-based finish pauses the VAD; resume if it was only a blip.
      const pausedByFinish = attempt.ending
      // Keep the live session: the real attempt that follows uses the same token.
      const session = attempt.session
      attempt.session = null
      resetAttempt()
      attempt.session = session
      if (pausedByFinish && waitingForRecording.value) {
        void vadRecorder.start().catch(() => showError?.('audioPlayer.microphoneError'))
      }
    },
    onFrameProcessed: (probabilities, frame) => {
      visualizer.setVadLevel(probabilities?.isSpeech || 0, frame)
      trackAttemptFrame(probabilities, frame)
    },
    getStream: microphones.getSharedStream,
  })

  function setPlayNextSubtitle(fn) {
    playNextSubtitle = fn
  }

  function clearAllTimers() {
    ;[pauseTimer, waitTimer, shadowingTimer, speechWaitTimer, recordingLimitTimer].forEach((timer) => {
      if (timer.value) {
        clearTimeout(timer.value)
        clearInterval(timer.value)
        timer.value = null
      }
    })
  }

  function clearSpeechWaitTimer() {
    if (!speechWaitTimer.value) return
    clearTimeout(speechWaitTimer.value)
    speechWaitTimer.value = null
  }

  function clearRecordingLimitTimer() {
    if (!recordingLimitTimer.value) return
    clearTimeout(recordingLimitTimer.value)
    recordingLimitTimer.value = null
  }

  function setupRecordingLimit() {
    clearRecordingLimitTimer()
    recordingLimitTimer.value = setTimeout(() => {
      if (
        readAlongMode.value === 'following' ||
        !waitingForRecording.value ||
        !isRecording.value ||
        isPaused.value
      ) return

      void vadRecorder.pause().catch(() => {})
      recordingLimitTimer.value = null
    }, Math.max(1000, Number(getMaxRecordingMs()) || 60000))
  }

  function setupSpeechWaitLimit() {
    clearSpeechWaitTimer()
    speechWaitTimer.value = setTimeout(() => {
      if (
        readAlongMode.value === 'following' ||
        !waitingForRecording.value ||
        isRecording.value ||
        isPaused.value
      ) return

      // Nobody spoke: stop listening and show the player as paused. Rewind to the
      // sentence start, so pressing play replays it before asking again.
      speechWaitTimer.value = null
      const subtitle = displaySubtitles.value[activeRecordingIndex]
      stopRecording()
      visualizer.setVadLevel(0)
      isPaused.value = true
      if (subtitle) seekCurrent?.(Number(subtitle.start) || 0)
    }, maxSpeechWaitMs)
  }

  function setupPauseTimer() {
    if (!isReadAlongMode.value || !sound.value || currentSubtitleIndex.value === null || readAlongMode.value === 'following') return

    const subtitle = displaySubtitles.value[currentSubtitleIndex.value]
    if (!subtitle) return

    const subtitleIndex = currentSubtitleIndex.value
    if (pauseTimer.value) clearInterval(pauseTimer.value)

    // Loading the VAD model and opening the microphone can take several seconds
    // on the first run. Start both while the sentence is playing, before the UI
    // asks the user to speak.
    void ensureRecordingReady()

    pauseTimer.value = setInterval(async () => {
      if (
        readAlongMode.value === 'following' ||
        isPaused.value
      ) {
        clearInterval(pauseTimer.value)
        pauseTimer.value = null
        return
      }

      const currentTime = Number(sound.value?.seek?.())
      if (!Number.isFinite(currentTime) || currentTime < subtitle.end - 0.02) return

      clearInterval(pauseTimer.value)
      pauseTimer.value = null
      currentSubtitleIndex.value = subtitleIndex
      isPlaying.value = false
      isSystemPauseForRecording = true
      pauseCurrent?.({ suppressCallback: true })
      seekCurrent?.(Math.max(Number(subtitle.start) || 0, Number(subtitle.end) - 0.01))
      isSystemPauseForRecording = false

      const isReady = await ensureRecordingReady()
      if (
        !isReady ||
        !isReadAlongMode.value ||
        readAlongMode.value === 'following' ||
        isPaused.value ||
        currentSubtitleIndex.value !== subtitleIndex
      ) return

      await startRecording()
    }, 40)
  }

  function setupFollowingTimer() {
    clearAllTimers()
    if (!isReadAlongMode.value || !sound.value || currentSubtitleIndex.value === null || readAlongMode.value !== 'following') return

    const subtitle = displaySubtitles.value[currentSubtitleIndex.value]
    if (!subtitle) return

    const subtitleIndex = currentSubtitleIndex.value

    pauseTimer.value = setInterval(() => {
      if (
        readAlongMode.value !== 'following' ||
        isPaused.value
      ) {
        clearInterval(pauseTimer.value)
        pauseTimer.value = null
        return
      }

      const currentTime = Number(sound.value?.seek?.())
      if (!Number.isFinite(currentTime) || currentTime < subtitle.end - 0.02) return

      clearInterval(pauseTimer.value)
      pauseTimer.value = null

      currentSubtitleIndex.value = subtitleIndex
      isPlaying.value = false
      pauseCurrent?.({ suppressCallback: true })
      seekCurrent?.(Math.max(Number(subtitle.start) || 0, Number(subtitle.end) - 0.01))
      isWaitingForShadowing.value = true
      shadowingStartTime.value = Date.now()
      shadowingDuration.value = Math.max(0, subtitle.end - subtitle.start + followingInterval.value)

      shadowingTimer.value = setTimeout(() => {
        if (readAlongMode.value === 'following' && !isPaused.value && !isPausedWhileShadowing.value) {
          isWaitingForShadowing.value = false
          playNextSubtitle?.(subtitleIndex)
        }
      }, shadowingDuration.value * 1000)
    }, 40)
  }

  async function startRecording() {
    if (!isReadAlongMode.value || readAlongMode.value === 'following' || currentSubtitleIndex.value === null) return

    try {
      const isReady = await ensureRecordingReady()
      if (
        !isReady ||
        !isReadAlongMode.value ||
        readAlongMode.value === 'following' ||
        currentSubtitleIndex.value === null ||
        isPaused.value
      ) return

      activeRecordingIndex = currentSubtitleIndex.value
      isRecording.value = false
      resetAttempt()
      visualizer.setVadLevel(0)
      visualizer.visualize()
      vadRecorder.setRedemptionMs(
        isRetelling.value ? RETELLING_PAUSE_MS : unfinishedPauseMs.value + VAD_PAUSE_MARGIN_MS,
      )
      await vadRecorder.start()
      waitingForRecording.value = true
      openLiveSession()
      setupSpeechWaitLimit()
    } catch (error) {
      waitingForRecording.value = false
      showError?.('audioPlayer.microphoneError')
    }
  }

  function stopRecording() {
    originalRecorder.cancel()
    resetRecordingFlags()
    void vadRecorder.pause().catch(() => {})
  }

  function resetRecordingFlags() {
    clearSpeechWaitTimer()
    clearRecordingLimitTimer()
    waitingForRecording.value = false
    isRecording.value = false
    activeRecordingIndex = null
    resetAttempt()
    visualizer.stopVisualizer()
  }

  async function finishVadRecording(audio) {
    if (!isReadAlongMode.value || readAlongMode.value === 'following' || !waitingForRecording.value || isCompletingRecording) return

    isCompletingRecording = true
    clearRecordingLimitTimer()
    const recordingIndex = activeRecordingIndex
    const finishedAttempt = attempt
    attempt = createAttempt()
    pauseProgress.value = 0
    isHoldingForUnfinished.value = false
    waitingForRecording.value = false
    isRecording.value = false
    activeRecordingIndex = null
    visualizer.stopVisualizer()
    const originalRecordingPromise = originalRecorder.stop()

    try {
      await vadRecorder.pause()
    } catch {} finally {
      isCompletingRecording = false
    }

    if (recordingIndex === null || !audio?.length) {
      finishedAttempt.session?.cancel()
      return
    }
    // An ended attempt always moves on, however much of the sentence was read: the
    // learner's voice alone drives smart read-along.
    const session = finishedAttempt.session
    if (session) {
      lingerLiveCaption()
      void session.finish()
    }

    const originalRecording = await originalRecordingPromise.catch(() => null)
    const recording = recordingLibrary.saveRecording(recordingIndex, audio, originalRecording)
    onRecordingReady?.({
      index: recordingIndex,
      blob: recording.blob,
      duration: recording.duration,
      subtitle: displaySubtitles.value[recordingIndex],
      liveAssessment: session?.assessment,
    })
    if (!isPaused.value && recordingIndex < displaySubtitles.value.length - 1) {
      playNextSubtitle?.(recordingIndex)
    } else {
      isPlaying.value = false
    }
  }

  function closeMediaStream() {
    originalRecorder.destroy()
    resetRecordingFlags()
    void vadRecorder.destroy().catch(() => {})
    microphones.releaseSharedStream()
  }

  async function toggleReadAlongMode() {
    if (sound.value) {
      sound.value.stop()
      isPlaying.value = false
    }

    if (isReadAlongMode.value) {
      isReadAlongMode.value = false
      void saveReadAlongPreference(false)
      resetReadAlongState()
      return
    }

    isReadAlongMode.value = true
    if (readAlongMode.value !== 'following') {
      const isReady = await ensureRecordingReady()
      if (!isReady) {
        isReadAlongMode.value = false
        return
      }
    }
    void saveReadAlongPreference(true)
  }

  async function refreshMicrophoneDevices(requestPermission = false) {
    try {
      await microphones.refreshMicrophones({ requestPermission })
      return true
    } catch {
      if (requestPermission) showError?.('audioPlayer.microphoneError')
      return false
    }
  }

  async function setMicrophoneDevice(deviceId) {
    const normalizedDeviceId = String(deviceId || '')
    if (normalizedDeviceId === microphones.selectedMicrophoneId.value) return

    const shouldRestart = (
      isReadAlongMode.value &&
      readAlongMode.value !== 'following' &&
      waitingForRecording.value
    )

    microphones.selectMicrophone(normalizedDeviceId)
    originalRecorder.destroy()
    // 换了麦克风：关闭旧流，下次开始录音时按新设备重新申请
    microphones.releaseSharedStream()
    clearSpeechWaitTimer()
    waitingForRecording.value = false
    isRecording.value = false
    visualizer.stopVisualizer()
    visualizer.setVadLevel(0)

    try {
      await vadRecorder.pause()
      if (shouldRestart) {
        await startRecording()
      }
    } catch {
      waitingForRecording.value = false
      showError?.('audioPlayer.microphoneError')
    }
  }

  async function initializeVad() {
    try {
      await vadRecorder.prepare()
      return true
    } catch (error) {
      showError?.('audioPlayer.microphoneError')
      return false
    }
  }

  async function ensureRecordingReady() {
    if (usesLiveRecognition.value) liveSpeech.prepare()
    if (vadRecorder.isPrepared.value && originalRecorder.isPrepared.value) return true
    if (recordingPreparationPromise) return recordingPreparationPromise

    recordingPreparationPromise = (async () => {
      if (!vadRecorder.isPrepared.value) {
        const hasMicrophoneAccess = await refreshMicrophoneDevices(true)
        if (!hasMicrophoneAccess) return false
        const vadReady = await initializeVad()
        if (!vadReady) return false
      }
      if (!originalRecorder.isPrepared.value) {
        await originalRecorder.prepare().catch(() => false)
      }
      return true
    })()

    try {
      return await recordingPreparationPromise
    } finally {
      recordingPreparationPromise = null
    }
  }

  function onPause() {
    if (!sound.value) return
    isPlaying.value = false
    if (!isReadAlongMode.value) return
    if (isSystemPauseForRecording || waitingForRecording.value) return
    if (readAlongMode.value !== 'following' && !isPaused.value) {
      void startRecording()
    }
  }

  // Runs on every real playback start. Whatever started it, playback in read-along
  // must stop at the sentence end; a start the learner did not ask for (a player
  // catching up on an earlier play request) is undone right away.
  function onPlaybackStarted() {
    if (!isReadAlongMode.value) return

    const shouldBeSilent = isPaused.value ||
      waitingForRecording.value ||
      isRecording.value ||
      isWaitingForShadowing.value ||
      isPausedWhileShadowing.value
    if (shouldBeSilent) {
      pauseCurrent?.({ suppressCallback: true })
      isPlaying.value = false
      return
    }

    if (readAlongMode.value !== 'following' && !pauseTimer.value) setupPauseTimer()
    if (readAlongMode.value === 'following' && !pauseTimer.value && !shadowingTimer.value) setupFollowingTimer()
  }

  function resetReadAlongState() {
    clearAllTimers()
    clearTimeout(liveCaptionTimer)
    isLiveCaptionLingering.value = false
    closeMediaStream()
    recordingLibrary.stopPlayingRecordings()
    waitingForRecording.value = false
    isWaitingForShadowing.value = false
    isPausedWhileShadowing.value = false
    isPaused.value = false
    visualizer.setVadLevel(0)
  }

  watch(readAlongMode, async (newMode) => {
    clearAllTimers()
    if (sound.value) {
      pauseCurrent?.({ suppressCallback: true })
      isPlaying.value = false
    }

    if (newMode !== 'following' && isReadAlongMode.value) {
      stopRecording()
      await ensureRecordingReady()
    } else {
      closeMediaStream()
    }
  })

  watch(followingInterval, () => {
    if (readAlongMode.value === 'following' && isPlaying.value) {
      setupFollowingTimer()
    }
  })

  watch(vadRedemptionMs, (value) => {
    window.localStorage.setItem(FINISHED_PAUSE_STORAGE_KEY, String(value))
  })

  watch(unfinishedPauseMs, (value) => {
    window.localStorage.setItem(UNFINISHED_PAUSE_STORAGE_KEY, String(value))
  })

  function cleanupReadAlong() {
    resetReadAlongState()
  }

  return {
    isReadAlongMode,
    readAlongMode,
    isRecording,
    isPaused,
    isWaitingForShadowing,
    isPausedWhileShadowing,
    waitingForRecording,
    recordings: recordingLibrary.recordings,
    recordingDurations: recordingLibrary.recordingDurations,
    totalRecordingTime: recordingLibrary.totalRecordingTime,
    currentRecordingTime: recordingLibrary.currentRecordingTime,
    isPlayingRecordings: recordingLibrary.isPlayingRecordings,
    playingRecordingIndex: recordingLibrary.playingRecordingIndex,
    followingInterval,
    vadRedemptionMs,
    unfinishedPauseMs,
    pauseProgress,
    isHoldingForUnfinished,
    liveTranscript,
    isLiveCaptionLingering,
    liveRecognitionStatus,
    liveRecognitionUnavailableReason: liveSpeech.unavailableReason,
    finishSpeaking,
    shadowingStartTime,
    shadowingDuration,
    microphoneDevices: microphones.microphoneDevices,
    selectedMicrophoneId: microphones.selectedMicrophoneId,
    isLoadingMicrophones: microphones.isLoadingMicrophones,
    refreshMicrophoneDevices,
    setMicrophoneDevice,
    setPlayNextSubtitle,
    toggleReadAlongMode,
    startRecording,
    stopRecording,
    closeMediaStream,
    clearAllTimers,
    onPause,
    onPlaybackStarted,
    setupPauseTimer,
    setupFollowingTimer,
    playRecording: recordingLibrary.playRecording,
    playAllRecordings: recordingLibrary.playAllRecordings,
    stopPlayingRecordings: recordingLibrary.stopPlayingRecordings,
    downloadRecordings: recordingLibrary.downloadRecordings,
    visualize: visualizer.visualize,
    microphoneLevel: visualizer.microphoneLevel,
    microphoneBars: visualizer.microphoneBars,
    cleanupReadAlong,
  }
}
